import React, { useEffect, useState } from 'react'
import MapUI from './MapUI'
import GoogleMap from './GoogleMap'
import useGeolocation, { isLocation } from '../queries/currentLocation'

const GOOGLE_MAPS_URL = 'https://www.google.com/maps/dir/?api=1'

const TravelModes = {
  WALKING: 'WALKING',
}

const POI_ZOOM = 18

const calculateAndDisplayRoute = async (
  directionsService,
  directionsRenderer,
  origin,
  destination
) => {
  const response = await directionsService.route({
    origin: origin,
    destination: destination,
    travelMode: TravelModes.WALKING,
  })
  try {
    directionsRenderer.setDirections(response)
  } catch (e) {
    window.alert('Route kon niet berekend worden.')
    throw e
  }
}

const Map = ({ event, parcours, atmosphericareas, pois }) => {
  const [baseMap, setBaseMap] = useState(null)
  const [poiLayers, setPoiLayers] = useState([])
  const [poiContent, setPoiContent] = useState(null)
  const [currentLocationMarker, setCurrentLocationMarker] = useState(null)
  const [directionsService, setDirectionsService] = useState(undefined)
  const [directionsRenderer, setDirectionsRenderer] = useState(undefined)
  const currentLocation = useGeolocation()

  useEffect(() => {
    if (isLocation(currentLocation) && currentLocationMarker) {
      currentLocationMarker.setPosition({
        lat: currentLocation.latitude,
        lng: currentLocation.longitude,
      })
      currentLocationMarker.setVisible(true)
    }
  }, [currentLocationMarker, currentLocation])

  const generateRoute = () => {
    /**
     * Generates a route if location of user is known,
     * else it lauches google maps with the poi location as destination.
     * If a route is generated, it returns true, which is used to show the navigation buttons.
     */
    if (isLocation(currentLocation)) {
      calculateAndDisplayRoute(
        directionsService,
        directionsRenderer,
        {
          lat: currentLocation.latitude,
          lng: currentLocation.longitude,
        },
        poiContent.getGeometry().get()
      )
      return true
    } else {
      launchGoogleMaps()
      return false
    }
  }

  const removeRoute = () => {
    directionsRenderer.setDirections({ routes: [] })
    setPoiContent(null)
  }

  // In the case of polygons and atmospheric areas, we create a deeplink to another application (perceel 1)
  // TODO: deeplinkIds should be managed somewhere else
  const generateTravelAdvice = (poiContent) => {
    let deeplinkId = ''
    switch (poiContent.j.name) {
      case 'Station':
        deeplinkId = '9d92a855-9693-46d8-bb34-3cffd65e8bf9'
        break
      case 'Ginneken':
        deeplinkId = 'a237d980-3765-4882-8556-a71c430e0f2b'
        break
      case 'Cooth':
        deeplinkId = 'aab23c7e-a8d9-46e6-8ebb-93d962cce8ad'
        break
      case 'Haven':
        deeplinkId = '125d88ba-3e08-429e-bc63-39f68a1e1686'
        break
      case 'Veemarkt':
        deeplinkId = '5e6495b5-9b50-4eb8-9a70-a75edefccfd2'
        break
      case 'Valkenberg':
        deeplinkId = 'b4e46623-186f-446f-9685-7b4b4a430dad'
        break
      case 'Hart':
        deeplinkId = '08c98595-a0f2-48bc-ba2f-68cdd40d9d85'
        break
      case 'Bosch':
        deeplinkId = '46cd129b-b1ad-488a-8663-794d17a73994'
        break
      case 'Haagdijk':
        deeplinkId = '37e04c10-d4e8-4699-aaf4-0b658da19882'
        break
      case 'Chasse':
        deeplinkId = '1b715dc1-3a7c-4012-868e-d3caed2ba9bd'
    }
    window.open(`https://www.bereikbaarbreda.nl/?deeplink=${deeplinkId}`)
  }

  const launchGoogleMaps = () => {
    const destination = poiContent.getGeometry().get()
    window.open(
      `${GOOGLE_MAPS_URL}&destination=${destination.lat()},${destination.lng()}&travelmode=${TravelModes.WALKING.toLowerCase()}&dir_action=navigate`
    )
  }

  useEffect(() => {
    if (poiContent) {
      let geometry = poiContent.getGeometry()
      // if geometry is polygon we dont want to pan
      if (geometry.get) {
        baseMap.panTo(geometry.get('lat'), geometry.get('lng'))
        baseMap.setZoom(POI_ZOOM)
      }
    }
  }, [poiContent])

  useEffect(() => {
    // move custom mapUI inside Google Maps DIV after instantiation
    const mapDiv = document.getElementById('map').getElementsByTagName('div')[0]
    if (mapDiv?.nodeType) {
      mapDiv.appendChild(document.getElementById('mapUI'))
    }
  }, [baseMap])

  return (
    <React.Fragment>
      <MapUI
        event={event}
        baseMap={baseMap}
        poiLayers={poiLayers}
        poiContent={poiContent}
        setPoiContent={setPoiContent}
        generateRoute={generateRoute}
        generateTravelAdvice={generateTravelAdvice}
        launchGoogleMaps={launchGoogleMaps}
        removeRoute={removeRoute}
      />
      <GoogleMap
        setBaseMap={setBaseMap}
        event={event}
        parcours={parcours}
        atmosphericareas={atmosphericareas}
        pois={pois}
        setPoiLayers={setPoiLayers}
        setPoiContent={setPoiContent}
        setCurrentLocationMarker={setCurrentLocationMarker}
        setDirectionsService={setDirectionsService}
        setDirectionsRenderer={setDirectionsRenderer}
      />
    </React.Fragment>
  )
}

export default Map
